import { ContentContainerStub, FeatureApp } from '@oneaudi/falcon-tools';
import { FootnoteEngineAem, GlobalDisclaimerAem } from '~/app/market-config';
import { MarketIdentifier } from '~/app/market-config/models/market-identifier';
import { AvailableKeyFact } from '~/app/market-config/models/technical-key-facts';
import { StockCarConfig, StockCarConfigFalcon } from '~/app/models/configurator-options';

export type OmnibusLayerType = { enabled: boolean; url: string };

export type Combination =
	| 'trimLine'
	| 'equipmentPackages'
	| 'paint'
	| 'paint[bodyPaint]'
	| 'paint[paintTypes]'
	| 'headlights'
	| 'interiorPackages'
	| 'interior[covers]'
	| 'packages'
	| 'rims'
	| 'equipmentHighlights'
	| 'steeringWheels';

export type CategoryDisclaimer = {
	step: Combination;
	disclaimer?: string;
};

export type CategoryDisclaimerFalcon = {
	stepName: Combination;
	disclaimer?: string;
};

export type AemMarketConfig = {
	market: MarketIdentifier;
	language: string;
	carlineId: string;
	hideStandardAccessories?: boolean;
	stockCarConfig?: StockCarConfig;
	enableDynamicSummaryFooterButton?: boolean;
	nbas?: Array<NextBestActionType>;
	additionalPrimaryNba?: NextBestActionType;
	combinations?: Array<Combination>;
	categoryDisclaimers?: Array<CategoryDisclaimer>;
	summaryFooterCta?: NextBestActionType;
	secondarySummaryCta?: NextBestActionType;
	disablePdfNba?: boolean;
	omnibus?: OmnibusLayerType;
	deliveryCostUrl?: string;
	showAccessoriesInclusive?: boolean;
	showAccessoriesExclusive?: boolean;
	enableIncentives?: boolean;
	specialOfferUrl?: string;
	enableFinancingServices?: boolean;
	hideMonthlyRates?: boolean;
	enablePartsRateCalculation?: boolean;
	showCaESummaryText?: boolean;
	showCaETextEngineCard?: boolean;
	engineCardKeyfacts?: AvailableKeyFact[];
	useIframe?: boolean;
	extendedTechnicalAttributes?: {
		wltp?: string[];
	};
	enableEngineFilter?: boolean;
	enable3d?: boolean;
	globalDisclaimer?: GlobalDisclaimerAem;
	leanCheckoutEnabled?: boolean;
	footnoteEngine?: FootnoteEngineAem;
};

export type NextBestActionType = {
	icon: string;
	trackingName: string;
	labelText: string;
	description: string;
	url: string;
	target: string;
	botID?: string;
};

export const AEM_MARKET_CONFIG: AemMarketConfig = {
	market: 'DE',
	language: 'de',
	carlineId: 'q8etron',
	stockCarConfig: undefined,
	enableDynamicSummaryFooterButton: false,
	nbas: [],
	additionalPrimaryNba: undefined,
	combinations: [],
	summaryFooterCta: undefined,
	secondarySummaryCta: undefined,
	disablePdfNba: undefined,
	hideStandardAccessories: undefined,
	leanCheckoutEnabled: false,
};

export type FalconFields = {
	market: MarketIdentifier;
	language: string;
	carlineId: string;
	hideStandardAccessories?: boolean;
	enableDynamicSummaryFooterButton?: boolean;
	stockCarConfig: FalconStockCarType;
	nbas: Array<FalconNextBestActionType>;
	additionalPrimaryNba: FalconNextBestActionType;
	configuratorSteps: Array<FalconConfiguratorStepType>;
	// for compatibility: new settings support an array, old one was just one item
	summaryFooterCta: Array<FalconNextBestActionType> | FalconNextBestActionType;
	secondarySummaryCta: FalconNextBestActionType;
	disablePdfNba?: boolean;
	omnibusUrl: string;
	deliveryCostUrl?: string;
	showAccessoriesInclusive?: boolean;
	showAccessoriesExclusive?: boolean;
	enableIncentives?: boolean;
	specialOfferUrl?: string;
	enableFinancingServices?: boolean;
	hideMonthlyRates?: boolean;
	showCaESummaryText?: boolean;
	showCaETextEngineCard?: boolean;
	engineCardKeyfacts: AvailableKeyFact[];
	extendedTechnicalAttributes_wltp: string[];
	enableEngineFilter: boolean;
	enable3d: boolean;
	globalDisclaimer: FalconGlobalDisclaimerType;
	leanCheckoutEnabled: boolean;
	footnoteEngine: FeatureApp | ContentContainerStub;
};

export type FalconNextBestActionType = {
	__type?: 'aem-headless';
	fields: NextBestActionType;
};

export type FalconConfiguratorStepType = {
	__type?: 'aem-headless';
	fields: CategoryDisclaimerFalcon;
};

export type FalconStockCarType = {
	__type?: 'aem-headless';
	fields: StockCarConfigFalcon;
};

export type FalconGlobalDisclaimerType = {
	__type?: 'aem-headless';
	fields: GlobalDisclaimerAem;
};
